<template>
  <div class="education-center">
    <a-lazy-hydrate when-visible>
      <a-breadcrumbs v-bind="breadcrumbsProps" />
    </a-lazy-hydrate>

    <a-lazy-hydrate when-visible>
      <a-join-telegram
        class="education-center__telegram"
        :channel-type="telegramChannel"
      />
    </a-lazy-hydrate>

    <h1 class="education-center__title page-title">
      {{ pageTitle }}
    </h1>

    <title-text-section
      v-if="isTitleTextSectionVisible"
      :title="TitleTop"
      :text="TextTop"
    />

    <education-news-list
      :news-list="Articles"
      :video-settings="VideoSettings"
    />

    <title-text-section
      v-if="isTitleTextSectionVisible"
      :title="TitleBottom"
      :text="TextBottom"
    />

    <a-sticky-banner
      :sticky-position="STICKY_BANNER_POSITION.BOTTOM"
      :banner-settings="BANNER_SETTINGS.ALL_NEWS.STICKY_TO_BOTTOM"
    />
  </div>
</template>

<script>
import mixins from '@/utils/mixins'
import {
  METHOD_NAME_TO_RUN_ON_LOGOUT,
  METHOD_NAME_TO_RUN_ON_LOGIN
} from '@/utils/mixins/run-method-on-login-logout'
import EducationNewsList from '@/components/_sections/education-center/EducationNewsList'
import ABreadcrumbs, { BREADCRUMB_NAMING } from 'shared/ABreadcrumbs'
import AJoinTelegram from 'shared/AJoinTelegram'
import { CHANNEL_TYPE } from 'shared/AJoinTelegram/enums'
import { SPECIAL_CATEGORY } from 'enums/special-categories'
import { PAGE_SCHEME_TYPE } from '@/utils/enums/pageSchemes'
import { ROUTE_NAME } from 'enums/routes'
import AStickyBanner from 'shared/AStickyBanner'
import { STICKY_BANNER_POSITION } from 'enums/banners/sticky-banner'
import { BANNER_SETTINGS } from 'enums/banners/banner-settings'
import { applyFallback } from '@/utils/helpers'
import { METHOD_NAME, PENDING_ACTION } from 'enums/pending-actions'
import { hydrateWhenVisible } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'
import { checkIfNonCanonicalPath } from '@/utils/helpers/canonical-links'
import { generateCategoryUrl } from '@/utils/helpers/url-generators'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'

const SPECIAL_CATEGORY_PAGE_TITLE = {
  [SPECIAL_CATEGORY.CRYPTOCURRENCY]: 'Cryptocurrency Education Center',
  [SPECIAL_CATEGORY.FOREX]: 'Fx Industry Education Center',
  [SPECIAL_CATEGORY.FINTECH]: 'Fintech Education Center'
}

const MODEL = {
  VideoSettings: {
    Video: null,
    Title: '',
    StartDate: '',
    EndDate: ''
  },
  Articles: [
    {
      CategorySlug: '',
      SubCategorySlug: '',
      ArticleSlug: '',
      Topic: '',
      Tldr: '',
      LogoImage: null,
      LogoVideo: null,
      PublishedOn: '',
      Order: 0,
      Id: '',
      IsDisclaimerVisible: false
    }
  ],
  CategoryName: '',
  CategorySlug: '',
  SubCategoryName: '',
  SubCategorySlug: '',
  TitleTop: '',
  TextTop: '',
  TitleBottom: '',
  TextBottom: ''
}

export default {
  name: 'EducationCenter',
  mixins: [
    mixins.refreshDataHandler,
    mixins.runMethodOnLoginLogout,
    mixins.setPageLoaded,
    mixins.refreshDataListenersFactory({
      refreshHandlerByType: {
        [PENDING_ACTION.SAVE_ARTICLE]: METHOD_NAME.UPDATE_IS_SAVED_VALUE
      }
    })
  ],
  components: {
    TitleTextSection: hydrateWhenVisible(
      () => import('sections/education-center/TitleTextSection'),
      { props: ['title', 'text'] }
    ),
    AStickyBanner,
    EducationNewsList,
    ABreadcrumbs,
    AJoinTelegram
  },
  head() {
    const pageName = `${this.$helper.capitalize(
      this.parentCategorySlug
    )} Education Center`

    return this.$generateMetaTags({
      titleParam: pageName,
      descriptionParam: pageName,
      schemes: [
        { type: PAGE_SCHEME_TYPE.ORGANIZATION },
        ...this.$helper.generateVideoSchemasFromResponse([
          this.VideoSettings?.Video,
          ...this.Articles.map(article => article.LogoVideo)
        ])
      ],
      canonicalUrl: this.canonicalUrl
    })
  },
  async asyncData(ctx) {
    const { path: currentPath, fullPath } = ctx.route
    const { category: parentCategorySlug } = ctx.route.params

    try {
      const { dispatch } = ctx.store

      const responseData = await dispatch('articles/requestEducationArticles', {
        parentCategorySlug
      })

      const {
        VideoSettings,
        Articles,
        CategoryName,
        CategorySlug,
        SubCategoryName,
        SubCategorySlug,
        TitleTop,
        TextTop,
        TitleBottom,
        TextBottom
      } = applyFallback(responseData, MODEL)

      const canonicalUrl = generateCategoryUrl(
        { CategorySlug, SubCategorySlug },
        ctx
      )

      if (checkIfNonCanonicalPath(ctx, { currentPath, canonicalUrl })) {
        redirectHandler({ ctx, to: canonicalUrl })
        return
      }

      return {
        canonicalUrl,
        Articles,
        VideoSettings,
        CategoryName,
        CategorySlug,
        SubCategorySlug,
        SubCategoryName,
        TitleTop,
        TextTop,
        TitleBottom,
        TextBottom
      }
    } catch (err) {
      ctx.app.$error(err, ctx)
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  data() {
    return {
      canonicalUrl: this.$route.path,
      Articles: [],
      VideoSettings: null,
      CategoryName: '',
      CategorySlug: '',
      SubCategoryName: '',
      SubCategorySlug: '',
      TitleTop: '',
      TextTop: '',
      TitleBottom: '',
      TextBottom: '',
      STICKY_BANNER_POSITION,
      BANNER_SETTINGS
    }
  },
  computed: {
    isTitleTextSectionVisible() {
      return this.CategorySlug === SPECIAL_CATEGORY.FINTECH
    },
    breadcrumbsDynamicNames() {
      return {
        [ROUTE_NAME.CATEGORY]: this.CategoryName,
        [ROUTE_NAME.CATEGORY_EDUCATION_CENTER]: this.SubCategoryName
      }
    },
    breadcrumbsProps() {
      return {
        breadcrumbsDynamicNames: this.breadcrumbsDynamicNames,
        breadcrumbsNaming: BREADCRUMB_NAMING.KEEP_AS_IS
      }
    },
    telegramChannel() {
      return this.parentCategorySlug === SPECIAL_CATEGORY.CRYPTOCURRENCY
        ? CHANNEL_TYPE.CRYPTO
        : CHANNEL_TYPE.FM
    },
    parentCategorySlug() {
      return (this.$route.params.category || '').toLowerCase()
    },
    pageTitle() {
      return SPECIAL_CATEGORY_PAGE_TITLE[this.parentCategorySlug]
    }
  },
  methods: {
    [METHOD_NAME.UPDATE_IS_SAVED_VALUE](payload) {
      this.Articles = this.Articles.map(article => ({
        ...article,
        IsSaved: article.Id === payload.id ? payload.value : article.IsSaved
      }))
    },
    [METHOD_NAME_TO_RUN_ON_LOGOUT]() {
      this.Articles = this.$_refreshDataHandler_resetActionIconValues(
        this.Articles
      )
    },
    async [METHOD_NAME_TO_RUN_ON_LOGIN]() {
      const {
        Article
      } = await this.$_refreshDataHandler_getSavedIdsOfEntities([
        this.$_refreshDataHandler_savedEntity.ARTICLE,
        this.$_refreshDataHandler_savedEntity.TAG
      ])
      this.Articles = this.Articles.map(article => ({
        ...article,
        IsSaved: Article.includes(article.Id) ? true : article.IsSaved
      }))
    }
  }
}
</script>

<style lang="scss" scoped>
.education-center {
  &__title {
    margin-bottom: 40px;

    @include mobile {
      margin-bottom: 20px;
    }
  }

  &__telegram {
    margin-bottom: 10px;
  }
}
</style>
